import axios, { AxiosResponse } from 'axios';
import type {
  BIApprovalFilter,
  BIApprovalTableRow,
  BIControlPanelTab,
  BIFilterOptionsRequest,
  BIFilterValue,
  BIParameterTableRow,
  BIParametersFilter,
  BIPaginatedResponse,
  BISelectOption,
  BIStatusUpdateRequest,
  BIStatusUpdateResponse,
  BITableRequest,
} from '@/store/modules/bi-control-panel/bi-control-panel-types';
import { AuthenticationService } from './authentication-service';
import { Api } from './api';

const ENDPOINT = '/bi-control-panel';
const analyticsBffApi = axios.create({ baseURL: `${process.env.VUE_APP_API}/analytics-bff` });
analyticsBffApi.interceptors.request.use(AuthenticationService.requestTokenInterceptor);
analyticsBffApi.interceptors.response.use(
  (success) => success,
  AuthenticationService.errorResponseInterceptor,
);

type BIRawParameterTableRow = {
  id: number;
  financial_group: string | null;
  contract_id: number | null;
  contract: string | null;
  policy: string | null;
  subcontract_id: number | null;
  subcontract: string | null;
  subcontract_number: string | null;
  carrier: string | null;
  competency: string | null;
  invoice_value: number | string | null;
  status: boolean;
  update_date: string | null;
  update_user: string | null;
};

type BIRawApprovalTableRow = {
  id: number;
  financial_group: string | null;
  contract_id: number | null;
  policy: string | null;
  carrier: string | null;
  competency: string | null;
  status: boolean;
  update_date: string | null;
  update_user: string | null;
};

type BIRawStatusUpdateResponse = Partial<BIRawParameterTableRow & BIRawApprovalTableRow> & {
  id: number;
  status: boolean;
};

export default class BIControlPanelService {
  async fetchFinancialGroups<TFilter extends Record<string, BIFilterValue>>(
    _tab: BIControlPanelTab,
    params: BIFilterOptionsRequest<TFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BISelectOption>>> {
    return this.fetchFilterOptions('financialGroup', params);
  }

  async fetchContracts<TFilter extends Record<string, BIFilterValue>>(
    _tab: BIControlPanelTab,
    params: BIFilterOptionsRequest<TFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BISelectOption>>> {
    return this.fetchFilterOptions('contract', params);
  }

  async fetchSubcontracts<TFilter extends Record<string, BIFilterValue>>(
    _tab: BIControlPanelTab,
    params: BIFilterOptionsRequest<TFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BISelectOption>>> {
    const searchText = String(params.searchText || '').trim();
    const contractId = (params.filter as Record<string, unknown>).contract;
    const parentContractIds: number[] = contractId != null ? [Number(contractId)] : [];

    const body: Record<string, unknown> = { parentContractIds };

    if (searchText) {
      body.searchByLegalNameOrSubContractNumber = searchText;
    }

    const response = await analyticsBffApi.post<BIPaginatedResponse<BISelectOption>>(
      '/contract/subcontracts/paginated',
      body,
      { params: { page: params.page, size: params.size } },
    );

    return {
      ...response,
      data: this.normalizePaginatedResponse(response.data),
    };
  }

  async fetchCarriers<TFilter extends Record<string, BIFilterValue>>(
    _tab: BIControlPanelTab,
    params: BIFilterOptionsRequest<TFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BISelectOption>>> {
    return this.fetchFilterOptions('carrier', params);
  }

  async fetchCompetencies<TFilter extends Record<string, BIFilterValue>>(
    tab: BIControlPanelTab,
    params: BIFilterOptionsRequest<TFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BISelectOption>>> {
    return this.fetchBIControlPanelFilterOptions(tab, 'competencies', 'competencyName', params);
  }

  async fetchParametersTable(
    params: BITableRequest<BIParametersFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BIParameterTableRow>>> {
    const response = await analyticsBffApi.get<BIPaginatedResponse<BIRawParameterTableRow>>(
      '/bi-control-panel/parameter',
      { params: this.buildParametersTableQueryParams(params) },
    );

    return {
      ...response,
      data: this.normalizePaginatedResponse(
        response.data,
        (row) => this.normalizeParameterTableRow(row),
      ),
    };
  }

  async fetchApprovalTable(
    params: BITableRequest<BIApprovalFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BIApprovalTableRow>>> {
    const response = await analyticsBffApi.get<BIPaginatedResponse<BIRawApprovalTableRow>>(
      '/bi-control-panel/load-approval',
      { params: this.buildTableQueryParams(params) },
    );

    return {
      ...response,
      data: this.normalizePaginatedResponse(
        response.data,
        (row) => this.normalizeApprovalTableRow(row),
      ),
    };
  }

  async updateParameterStatus(
    payload: BIStatusUpdateRequest,
  ): Promise<AxiosResponse<BIStatusUpdateResponse>> {
    const response = await analyticsBffApi.put<BIRawStatusUpdateResponse>(
      `/bi-control-panel/parameter/${payload.id}/status`,
      { active: payload.approvalStatus },
    );

    return {
      ...response,
      data: this.normalizeStatusUpdateResponse(response.data),
    };
  }

  async updateApprovalStatus(
    payload: BIStatusUpdateRequest,
  ): Promise<AxiosResponse<BIStatusUpdateResponse>> {
    const response = await analyticsBffApi.put<BIRawStatusUpdateResponse>(
      `/bi-control-panel/load-approval/${payload.id}/status`,
      { active: payload.approvalStatus },
    );

    return {
      ...response,
      data: this.normalizeStatusUpdateResponse(response.data),
    };
  }

  private async fetchFilterOptions<TFilter extends Record<string, BIFilterValue>>(
    fieldKey: 'financialGroup' | 'contract' | 'carrier',
    params: BIFilterOptionsRequest<TFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BISelectOption>>> {
    const request = this.buildAnalyticsFilterOptionsRequest(fieldKey, params);
    const response = await analyticsBffApi.get<BIPaginatedResponse<BISelectOption>>(
      request.path,
      { params: request.params },
    );

    return {
      ...response,
      data: this.normalizePaginatedResponse(response.data),
    };
  }

  private fetchBIControlPanelFilterOptions<TFilter extends Record<string, BIFilterValue>>(
    tab: BIControlPanelTab,
    endpointPath: string,
    searchParamKey: string,
    params: BIFilterOptionsRequest<TFilter>,
  ): Promise<AxiosResponse<BIPaginatedResponse<BISelectOption>>> {
    return Api.get<BIPaginatedResponse<BISelectOption>>(
      `${ENDPOINT}/filters/${endpointPath}`,
      { params: this.buildBIControlPanelFilterOptionsQueryParams(tab, params, searchParamKey) },
    );
  }

  private buildAnalyticsFilterOptionsRequest(
    fieldKey: 'financialGroup' | 'contract' | 'carrier',
    params: BIFilterOptionsRequest<Record<string, BIFilterValue>>,
  ): { path: string; params: Record<string, string | number | boolean> } {
    const query: Record<string, string | number | boolean> = {
      page: params.page,
      size: params.size,
    };
    const searchText = String(params.searchText || '').trim();

    if (searchText) {
      const searchParamKey = fieldKey === 'contract' ? 'searchByLegalNameOrPolicy' : 'name';

      query[searchParamKey] = searchText;
    }

    if (fieldKey === 'contract' && params.filter.financialGroup) {
      query.financialGroupId = params.filter.financialGroup;
    }

    if (fieldKey === 'carrier' && params.filter.financialGroup) {
      query.financialGroupIds = params.filter.financialGroup;
    }

    return {
      path: fieldKey === 'financialGroup' ? '/financial-group/paginated' : `/${fieldKey === 'carrier' ? 'carrier' : 'contract'}/paginated`,
      params: query,
    };
  }

  private buildBIControlPanelFilterOptionsQueryParams(
    tab: BIControlPanelTab,
    params: BIFilterOptionsRequest<Record<string, BIFilterValue>>,
    searchParamKey: string,
  ): Record<string, string | number> {
    const query: Record<string, string | number> = {
      tab,
      page: params.page,
      size: params.size,
      ...this.mapFilterToQueryParams(params.filter),
    };

    const searchText = String(params.searchText || '').trim();

    if (searchText) {
      query[searchParamKey] = searchText;
    }

    return query;
  }

  private mapFilterToQueryParams(
    filter: Record<string, BIFilterValue>,
  ): Record<string, string | number> {
    return Object.entries(filter).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined) {
        acc[`${key}Id`] = value;
      }

      return acc;
    }, {} as Record<string, string | number>);
  }

  private buildTableQueryParams(
    params: BITableRequest<Record<string, BIFilterValue>>,
  ): Record<string, string | number> {
    const query: Record<string, string | number> = {
      page: params.page,
      size: params.size,
    };
    const filterMap: Record<string, string> = {
      financialGroup: 'financialGroupId',
      contract: 'contractId',
      subcontract: 'subcontractId',
      carrier: 'carrierId',
      competency: 'competency',
    };

    Object.entries(params.filter).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      const queryKey = filterMap[key];

      if (queryKey) {
        query[queryKey] = value;
      }
    });

    return query;
  }

  private buildParametersTableQueryParams(
    params: BITableRequest<Record<string, BIFilterValue>>,
  ): Record<string, string | number> {
    const query: Record<string, string | number> = {};
    const filterMap: Record<string, string> = {
      financialGroup: 'financialGroupId',
      contract: 'contractId',
      subcontract: 'subcontractId',
      carrier: 'carrierId',
      competency: 'competency',
    };

    Object.entries(params.filter).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      const queryKey = filterMap[key];

      if (queryKey) {
        query[queryKey] = value;
      }
    });

    return query;
  }

  private normalizeParameterTableRow(row: BIRawParameterTableRow): BIParameterTableRow {
    return {
      id: row.id,
      financialGroup: row.financial_group || '',
      contract: row.contract || '',
      subcontract: row.subcontract || '',
      carrier: row.carrier || '',
      competency: row.competency || '',
      invoiceValue: Number(row.invoice_value || 0),
      approvalStatus: Boolean(row.status),
      updateDate: row.update_date || '',
      updateUser: row.update_user || '',
    };
  }

  private normalizeApprovalTableRow(row: BIRawApprovalTableRow): BIApprovalTableRow {
    return {
      id: row.id,
      financialGroup: row.financial_group || '',
      contract: row.policy || '',
      carrier: row.carrier || '',
      competency: row.competency || '',
      approvalStatus: Boolean(row.status),
      updateDate: row.update_date || '',
      updateUser: row.update_user || '',
    };
  }

  private normalizeStatusUpdateResponse(response: BIRawStatusUpdateResponse): BIStatusUpdateResponse {
    return {
      id: response.id,
      approvalStatus: Boolean(response.status),
    };
  }

  private normalizePaginatedResponse<TRaw, TNormalized = TRaw>(
    response: BIPaginatedResponse<TRaw>,
    mapItem?: (item: TRaw) => TNormalized,
  ): BIPaginatedResponse<TNormalized> {
    const content = mapItem
      ? response.content.map(mapItem)
      : response.content as unknown as TNormalized[];

    return {
      ...response,
      content,
      page: typeof response.page === 'number' ? response.page : response.number || 0,
    };
  }
}
